import { useEffect } from "react";
import { useRouter, useRouterState, type NavigateOptions } from "@tanstack/react-router";
import { sitePage } from "./links";
import { isAuthPresentationPath } from "../../../shared/src/site-auth-query";

/**
 * A page of this site, as path, query and hash; null for anything a click opens whole, including
 * links marked data-reload (signing in or out, checkout), which start a fresh document.
 */
export function startHref(
  anchor: HTMLAnchorElement,
  page: (pathname: string) => boolean = sitePage,
): string | null {
  if (anchor.hasAttribute("download")) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  const url = new URL(anchor.href, location.href);
  if (
    anchor.hasAttribute("data-reload") &&
    !(
      document.documentElement.hasAttribute("data-publication") &&
      isAuthPresentationPath(url.pathname)
    )
  )
    return null;
  if (url.origin !== location.origin || !page(url.pathname)) return null;
  return url.pathname + url.search + url.hash;
}

// Links in authored pages are plain addresses; the router takes them whole as href.
const hrefOptions = (href: string) => ({ href }) as NavigateOptions;

function anchorFrom(target: EventTarget | null) {
  return target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
}

/**
 * Client navigation for the creator site. Links between its pages move within this document: the
 * header, footer and player stay, and only the next page's data is requested. The dashboard, the
 * member app, the API and files load a whole document.
 *
 * Intent warms route JavaScript for every viewer, without running guards or loaders. A visitor
 * without an account also gets public data ahead of a click. Private pages are requested only
 * when opened, and never reused from memory (site/start/router.tsx sets cache times to zero).
 */
export function useSiteNavigation({
  prefetch,
  enabled,
  page = sitePage,
  transitions = false,
}: {
  prefetch: boolean;
  enabled: boolean;
  /** Which paths are this site's pages; Drate's own host keeps the dashboard's paths whole. */
  page?: (pathname: string) => boolean;
  /**
   * Drate's own pages: a link to a place on the same page glides there, and the next page
   * cross-fades in (platform.css) instead of replacing this one at once.
   */
  transitions?: boolean;
}) {
  const router = useRouter();
  useEffect(() => {
    if (!enabled) return;
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = anchorFrom(event.target);
      const href = anchor && startHref(anchor, page);
      if (!href) return;
      // A link to a place on this same page scrolls there without a navigation: at once on a
      // creator's site, as the browser does, and gliding there on Drate's own pages.
      const url = new URL(href, location.href);
      if (url.hash && url.pathname === location.pathname && url.search === location.search) {
        if (!transitions) return;
        event.preventDefault();
        const glide = !matchMedia("(prefers-reduced-motion: reduce)").matches;
        void router.navigate({
          ...hrefOptions(href),
          hashScrollIntoView: { behavior: glide ? "smooth" : "auto", block: "start" },
        });
        return;
      }
      event.preventDefault();
      // A view switch on the same page (Simple and Detailed) keeps the reader where they are.
      const preserveScroll = anchor.hasAttribute("data-preserve-scroll");
      void router.navigate({
        ...hrefOptions(href),
        resetScroll: !preserveScroll,
        ...(transitions && { viewTransition: true }),
      });
    };
    let hoverTimer: ReturnType<typeof setTimeout> | undefined;
    const preload = (anchor: HTMLAnchorElement | null) => {
      const href = anchor && startHref(anchor, page);
      if (!href) return;
      // Another view of this same page (a filter, Simple or Detailed) has nothing new to load.
      if (new URL(href, location.href).pathname === location.pathname) return;
      if (prefetch && !anchor?.hasAttribute("data-no-prefetch")) {
        void router.preloadRoute(hrefOptions(href)).catch(() => undefined);
      } else {
        // Loading component code does not run a loader or retrieve any coaching.
        const url = new URL(href, location.href);
        for (const match of router.matchRoutes(
          url.pathname,
          Object.fromEntries(url.searchParams),
        )) {
          void router.loadRouteChunk(router.routesById[match.routeId]!)?.catch(() => undefined);
        }
      }
    };
    const onPointerOver = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      const anchor = anchorFrom(event.target);
      if (!anchor || anchor === anchorFrom(event.relatedTarget)) return;
      clearTimeout(hoverTimer);
      // A pointer passing over a link on its way elsewhere doesn't load it.
      hoverTimer = setTimeout(() => preload(anchor), 65);
    };
    const onPointerOut = (event: PointerEvent) => {
      if (anchorFrom(event.target) !== anchorFrom(event.relatedTarget)) clearTimeout(hoverTimer);
    };
    const onIntent = (event: Event) => preload(anchorFrom(event.target));
    document.addEventListener("click", onClick);
    document.addEventListener("pointerover", onPointerOver);
    document.addEventListener("pointerout", onPointerOut);
    document.addEventListener("touchstart", onIntent, { passive: true });
    document.addEventListener("focusin", onIntent);
    return () => {
      clearTimeout(hoverTimer);
      document.removeEventListener("click", onClick);
      document.removeEventListener("pointerover", onPointerOver);
      document.removeEventListener("pointerout", onPointerOut);
      document.removeEventListener("touchstart", onIntent);
      document.removeEventListener("focusin", onIntent);
    };
  }, [router, prefetch, enabled, page, transitions]);
}

const authPages = [
  "/login",
  "/signup",
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
];

/**
 * Sign-in and sign-up links return visitors to the page they were opened from
 * (site/src/lib/creator-auth.ts), including after client navigation.
 */
export function useAuthReturnLinks() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const link =
        event.target instanceof Element
          ? event.target.closest<HTMLAnchorElement>("[data-creator-auth-link]")
          : null;
      if (!link || authPages.includes(location.pathname)) return;
      const target = new URL(link.href);
      target.searchParams.set("returnTo", location.pathname + location.search + location.hash);
      link.href = target.pathname + target.search;
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}

/**
 * Loading feedback is independent of visual page transitions. Fast, cached navigations never
 * flash a line; slower ones show progress, then complete and fade when their content arrives.
 */
export function useNavigationPending() {
  const pending = useRouterState({ select: (state) => state.status === "pending" });
  useEffect(() => {
    const root = document.documentElement;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (pending) {
      if (root.dataset.navigationPending === "complete") delete root.dataset.navigationPending;
      timer = setTimeout(() => {
        root.dataset.navigationPending = "loading";
      }, 120);
    } else if (root.dataset.navigationPending === "loading") {
      root.dataset.navigationPending = "complete";
      timer = setTimeout(() => {
        delete root.dataset.navigationPending;
      }, 250);
    }
    return () => clearTimeout(timer);
  }, [pending]);
  useEffect(
    () => () => {
      delete document.documentElement.dataset.navigationPending;
    },
    [],
  );
}

/**
 * Reports each page a visitor sees, for the creator's Insights › Traffic tab
 * (site/src/lib/site-views.ts): once for the document, then after every client navigation. The
 * server decides what counts; previews and the editor frame never report.
 */
export function useSiteViews(enabled: boolean) {
  const router = useRouter();
  useEffect(() => {
    if (!enabled) return;
    let reported: string | null = null;
    let referrer = document.referrer;
    const report = () => {
      // A page Chrome prerenders is counted when the visitor opens it, not before.
      if ((document as Document & { prerendering?: boolean }).prerendering) return;
      const path = location.pathname;
      if (path === reported) return;
      reported = path;
      const from = referrer;
      // The next client navigation arrives from this page, so it isn't a new visit.
      referrer = location.href;
      // Social apps often omit the referrer. Send only the source tag, never the URL query. A
      // Google Ads click carries a click id, which becomes the tag alone, so paid search stands
      // apart from Google's free results.
      const query = new URLSearchParams(location.search);
      const source = ["gclid", "gbraid", "wbraid"].some((key) => query.has(key))
        ? "google-ads"
        : query.get("utm_source");
      const body = JSON.stringify({ path, referrer: from, source });
      if (navigator.sendBeacon?.("/_drate/view", body)) return;
      void fetch("/_drate/view", { method: "POST", body, keepalive: true }).catch(() => {});
    };
    document.addEventListener("prerenderingchange", report, { once: true });
    const unsubscribe = router.subscribe("onResolved", report);
    report();
    return () => {
      document.removeEventListener("prerenderingchange", report);
      unsubscribe();
    };
  }, [router, enabled]);
}
